import React from 'react';
import { SFButton, SFScrollable, SFTimeline } from 'sfui';
import styles from './ReportHistory.module.scss';
import { TourTooltip } from 'ui-smartforce-settings';
import { ReportListItemTooltipType } from '../../ReportList/ReportList';
import { AmendDisabledTooltip } from '../../AmendDisabledTooltip/AmendDisabledTooltip';
import { useReportHistory } from './Hooks/useReportHistory';

const agencyTooltipDescription: Record<ReportListItemTooltipType, string> = {
  default:
    'You can amend a report as many times as you need. Just click the “Amend Report” button. In the History section, you will see all the versions. Any version can be viewed by clicking on it.',
  disabled:
    'Please note that there are no reports available for editing. Please try again another time.'
};

export interface ReportHistoryItem {
  version: number;
  title: string;
  subtitle: string;
  chipText?: string;
  footer?: React.ReactElement;
}

export interface ReportHistoryProps {
  size?: 'medium' | 'large';
  items: ReportHistoryItem[];
  isAmendDisabled: boolean;
  isReportableDisabled: boolean;
  selectedIndex?: number;
  tooltipType?: ReportListItemTooltipType;
  reportStatus?: string;
  onItemClick: (item: ReportHistoryItem, index: number) => void;
  onReportableStatusButtonClick?: () => void;
  onAmend: () => void;
}

export const ReportHistory = ({
  size = 'medium',
  items,
  isAmendDisabled,
  isReportableDisabled,
  tooltipType,
  reportStatus,
  selectedIndex,
  onItemClick,
  onAmend,
  onReportableStatusButtonClick
}: ReportHistoryProps): React.ReactElement<ReportHistoryProps> => {
  const {
    location,
    tour,
    tourStep,
    onTourNext,
    onAmendClick,
    onTimelineItemClick,
    isAmendButtonDisabled
  } = useReportHistory({
    items,
    isAmendDisabled,
    reportStatus,
    onAmend,
    onItemClick
  });

  // Add amend button to the latest version (first item)
  if (items.length > 0) {
    items[0].footer = (
      <div className={styles.buttonsContainer}>
        <SFButton
          fullWidth
          size={size}
          variant="outlined"
          disabled={
            isAmendButtonDisabled || isReportableDisabled || selectedIndex !== 0
          }
          onClick={onReportableStatusButtonClick}
        >
          Change Reportable Status
        </SFButton>

        {(!tooltipType ||
          location.pathname === '/app/collaborated-reports') && (
          <AmendDisabledTooltip
            cause={isAmendDisabled ? 'no-permission' : 'archived'}
            isActive={isAmendButtonDisabled}
          >
            <div>
              <SFButton
                fullWidth
                size={size}
                variant="contained"
                disabled={isAmendButtonDisabled || selectedIndex !== 0}
                onClick={onAmendClick}
              >
                Amend Report
              </SFButton>
            </div>
          </AmendDisabledTooltip>
        )}

        {tooltipType &&
          location.pathname === '/app/agency-reports/contacts' && (
            <TourTooltip
              title="Amend your agency reports"
              description={agencyTooltipDescription[tooltipType]}
              step={1}
              lastStep={3}
              tourId={6}
              placement="bottom-end"
              preventOverflow
              topZIndex
              enterDelay={400}
            >
              <AmendDisabledTooltip
                cause={isAmendDisabled ? 'no-permission' : 'archived'}
                isActive={
                  isAmendButtonDisabled && (tour?.id !== 6 || tourStep !== 1)
                }
              >
                <SFButton
                  fullWidth
                  size={size}
                  variant="contained"
                  disabled={isAmendButtonDisabled || selectedIndex !== 0}
                  onClick={() => {
                    onTourNext({ tourId: 6, step: 1 });
                    onAmend();
                  }}
                >
                  Amend Report
                </SFButton>
              </AmendDisabledTooltip>
            </TourTooltip>
          )}

        {tooltipType && location.pathname === '/app/my-reports/contacts' && (
          <TourTooltip
            title="Amend your reports"
            description="You can amend a report as many times as you need. Just click the “Amend Report” button. In the History section, you will see all the versions. Any version can be viewed by clicking on it."
            step={1}
            lastStep={3}
            tourId={10}
            placement="bottom-end"
            topZIndex
            enterDelay={400}
          >
            <SFButton
              fullWidth
              size={size}
              variant="contained"
              disabled={isAmendButtonDisabled || selectedIndex !== 0}
              onClick={() => {
                onTourNext({ tourId: 10, step: 1 });
                onAmend();
              }}
            >
              Amend Report
            </SFButton>
          </TourTooltip>
        )}
      </div>
    );
  }

  return (
    <div className={styles.reportHistory}>
      <SFScrollable containerClassName={styles.content}>
        <h4>History</h4>

        <SFTimeline
          size={size}
          items={items}
          selectedIndex={selectedIndex}
          onItemClick={onTimelineItemClick}
        />
      </SFScrollable>
    </div>
  );
};
